import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { 
  Sprout, 
  ShieldCheck, 
  TrendingUp, 
  Truck, 
  ShoppingBag, 
  Bot, 
  MapPin, 
  BarChart3, 
  Users, 
  ArrowRight, 
  CheckCircle2, 
  Sparkles,
  Leaf,
  ChevronRight,
  Globe,
  Award
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const LandingPage = () => {
  const { isAuthenticated, user } = useAuth();

  const benefits = [
    {
      icon: Sprout,
      title: 'Soberanía Alimentaria',
      description: 'Impulsamos la producción agrícola comunal garantizando la seguridad y soberanía alimentaria local.'
    },
    {
      icon: ShieldCheck,
      title: 'Control de Insumos',
      description: 'Registro transparente y asignación eficiente de fertilizantes, semillas y herramientas para cada productor.'
    },
    {
      icon: TrendingUp,
      title: 'Planificación Inteligente',
      description: 'Monitoreo de rendimientos de cosecha, fechas óptimas de siembra y predicciones con Inteligencia Artificial.'
    },
    {
      icon: Truck,
      title: 'Logística y Distribución',
      description: 'Trazabilidad en tiempo real del transporte y despacho de la producción hacia mercados comunales.'
    }
  ];

  const modules = [
    {
      icon: Users,
      title: 'Gestión de Productores',
      desc: 'Censo y perfilamiento detallado de productores agrícolas y sus hectáreas cultivables.'
    },
    {
      icon: Leaf,
      title: 'Monitoreo de Cultivos',
      desc: 'Seguimiento por fases desde la siembra hasta la cosecha estimada con alertas climáticas.'
    },
    {
      icon: Bot,
      title: 'Consultor Agrícola IA',
      desc: 'Asistente virtual especializado en diagnóstico de plagas, suelos y recomendaciones nutricionales.'
    },
    {
      icon: ShoppingBag,
      title: 'Comercio Comunal',
      desc: 'Plataforma directa de intercambio y venta justa de rubros agrícolas sin intermediarios.'
    },
    {
      icon: MapPin,
      title: 'Geolocalización Comunal',
      desc: 'Mapa interactivo con hectáreas sembradas, rutas de despacho y ubicación de unidades productivas.'
    },
    {
      icon: BarChart3,
      title: 'Reportes y Analítica',
      desc: 'Indicadores ejecutivos clave para la toma de decisiones estratégicas de los líderes de la Comuna.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>SISCIAC - Sistema de Sobrenanía Alimentaria y Control Agrícola</title>
        <meta name="description" content="Plataforma de soberanía alimentaria y control de insumos agrícolas comunales." />
      </Helmet>

      <div className="min-h-screen bg-slate-900 text-gray-100 flex flex-col font-sans selection:bg-yellow-400 selection:text-slate-900">
        {/* Navigation Bar */}
        <header className="sticky top-0 z-50 bg-slate-900/90 backdrop-blur-md border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-700 rounded-xl flex items-center justify-center shadow-lg shadow-green-900/30 group-hover:scale-105 transition-transform">
                <Sprout className="w-7 h-7 text-yellow-300" />
              </div>
              <div>
                <span className="text-2xl font-black tracking-tight text-white flex items-center">
                  SISCIAC
                  <span className="ml-1.5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-yellow-400/20 text-yellow-300 border border-yellow-400/30 rounded-full">
                    v2.0
                  </span>
                </span>
                <p className="text-xs text-slate-400 font-medium">Soberanía Alimentaria Comunal</p>
              </div>
            </Link>

            {/* Nav Links */}
            <nav className="hidden md:flex items-center space-x-8 text-sm font-medium text-slate-300">
              <a href="#inicio" className="hover:text-yellow-400 transition-colors">Inicio</a>
              <a href="#beneficios" className="hover:text-yellow-400 transition-colors">Beneficios</a>
              <a href="#modulos" className="hover:text-yellow-400 transition-colors">Módulos</a>
              <a href="#comuna" className="hover:text-yellow-400 transition-colors">Comuna</a>
            </nav>

            {/* Auth Actions */}
            <div className="flex items-center space-x-3">
              {isAuthenticated ? (
                <Link
                  to={user?.role === 'admin' ? '/admin' : '/producer'}
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-semibold px-5 py-2.5 rounded-xl shadow-lg shadow-green-900/40 transition-all hover:scale-105"
                >
                  <span>Ir al Panel</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-sm font-semibold text-slate-200 hover:text-white px-4 py-2.5 rounded-xl border border-slate-700 hover:border-slate-500 hover:bg-slate-800 transition-all"
                  >
                    Iniciar Sesión
                  </Link>
                  <Link
                    to="/register"
                    className="inline-flex items-center space-x-1.5 bg-yellow-500 hover:bg-yellow-400 text-slate-950 font-bold text-sm px-5 py-2.5 rounded-xl shadow-lg shadow-yellow-500/20 transition-all hover:scale-105"
                  >
                    <span>Registro</span>
                    <Sparkles className="w-4 h-4 text-slate-950" />
                  </Link>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section id="inicio" className="relative pt-12 pb-24 lg:pt-20 lg:pb-32 overflow-hidden bg-slate-900">
          {/* Subtle Glow Backgrounds */}
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-green-600/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-10 right-10 w-[400px] h-[400px] bg-yellow-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              {/* Hero Text */}
              <motion.div 
                className="lg:col-span-7 space-y-6 text-center lg:text-left"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-full bg-slate-800/80 border border-slate-700 text-yellow-300 text-xs font-semibold">
                  <Award className="w-4 h-4 text-yellow-400" />
                  <span>Sistema de Información y Control Agrícola</span>
                </div>

                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight">
                  Impulsando la <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-300 to-green-400">Soberanía Alimentaria</span> Comunal
                </h1>

                <p className="text-lg text-slate-300 max-w-2xl mx-auto lg:mx-0 font-normal leading-relaxed">
                  SISCIAC es la plataforma tecnológica diseñada para optimizar la siembra, el control de insumos agrícolas y la logística de distribución en beneficio de los productores de la Comuna Simón Bolívar y del país.
                </p>

                <div className="pt-4 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                  <Link
                    to="/register"
                    className="w-full sm:w-auto inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold px-8 py-4 rounded-xl shadow-xl shadow-green-900/40 text-base transition-all hover:-translate-y-0.5"
                  >
                    <span>Unirse como Productor</span>
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/login"
                    className="w-full sm:w-auto inline-flex items-center justify-center space-x-2 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 font-semibold px-8 py-4 rounded-xl text-base transition-all"
                  >
                    <span>Acceso al Sistema</span>
                  </Link>
                </div>

                {/* Key Stats */}
                <div className="pt-10 grid grid-cols-3 gap-6 border-t border-slate-800/80">
                  <div>
                    <p className="text-3xl font-black text-yellow-400">100%</p>
                    <p className="text-xs text-slate-400 font-medium">Control Comunal</p>
                  </div>
                  <div>
                    <p className="text-3xl font-black text-green-400">+50</p>
                    <p className="text-xs text-slate-400 font-medium">Productores Registrados</p>
                  </div>
                  <div>
                    <p className="text-3xl font-black text-emerald-400">IA</p>
                    <p className="text-xs text-slate-400 font-medium">Asistencia Agrícola</p>
                  </div>
                </div>
              </motion.div>

              {/* Hero Feature Visual Card */}
              <motion.div 
                className="lg:col-span-5"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                <div className="relative mx-auto max-w-md bg-slate-800/90 border border-slate-700/80 rounded-2xl p-6 shadow-2xl shadow-green-950/50 backdrop-blur-xl">
                  {/* Top Bar Mockup */}
                  <div className="flex items-center justify-between pb-4 border-b border-slate-700/60 mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-green-600/20 border border-green-500/40 rounded-lg flex items-center justify-center">
                        <Sprout className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-white">Panel SISCIAC</h3>
                        <p className="text-[11px] text-yellow-400 font-medium">Comuna Simón Bolívar</p>
                      </div>
                    </div>
                    <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-ping" />
                  </div>

                  {/* Widget Preview */}
                  <div className="space-y-4">
                    <div className="bg-slate-900/90 border border-slate-700/50 rounded-xl p-4 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-400">Superficie Sembrada</p>
                        <p className="text-xl font-bold text-white">124.5 Ha</p>
                      </div>
                      <span className="px-2.5 py-1 bg-green-500/20 text-green-300 border border-green-500/30 rounded-lg text-xs font-semibold">
                        +18.4% ciclo
                      </span>
                    </div>

                    <div className="bg-slate-900/90 border border-slate-700/50 rounded-xl p-4 space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-300 font-medium">Distribución de Insumos</span>
                        <span className="text-yellow-400 font-bold">88%</span>
                      </div>
                      <div className="w-full bg-slate-800 rounded-full h-2">
                        <div className="bg-gradient-to-r from-yellow-400 to-green-500 h-2 rounded-full w-[88%]" />
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-green-950/60 to-slate-900 border border-green-600/30 rounded-xl p-4 flex items-center space-x-3">
                      <Bot className="w-8 h-8 text-yellow-300 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-white">Consultor IA Agrícola Activo</p>
                        <p className="text-[11px] text-slate-300">Recomendación para siembra de Maíz y Caraota en suelos comunitarios.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section id="beneficios" className="py-20 bg-slate-950 border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-yellow-400 font-bold text-xs uppercase tracking-widest px-3 py-1 bg-yellow-400/10 border border-yellow-400/20 rounded-full">
                ¿Por qué usar SISCIAC?
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-4 tracking-tight">
                Diseñado para Potenciar la Producción Agroalimentaria
              </h2>
              <p className="text-slate-400 text-base mt-3">
                Herramientas tecnológicas avanzadas al servicio del campesino, el productor y los consejos comunales.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((b, idx) => {
                const Icon = b.icon;
                return (
                  <div key={idx} className="bg-slate-900 border border-slate-800 hover:border-green-500/50 rounded-2xl p-6 transition-all hover:-translate-y-1 shadow-lg group">
                    <div className="w-12 h-12 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center justify-center mb-5 text-green-400 group-hover:bg-green-500 group-hover:text-slate-950 transition-colors">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{b.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{b.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Modules Section */}
        <section id="modulos" className="py-20 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-green-400 font-bold text-xs uppercase tracking-widest px-3 py-1 bg-green-400/10 border border-green-400/20 rounded-full">
                Ecosistema Integrado
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-4 tracking-tight">
                Módulos Especializados del Sistema
              </h2>
              <p className="text-slate-400 text-base mt-3">
                Cada sección responde a una necesidad real del campo y la organización popular.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {modules.map((m, idx) => {
                const Icon = m.icon;
                return (
                  <div key={idx} className="bg-slate-950/80 border border-slate-800 rounded-2xl p-6 hover:border-yellow-500/40 transition-all">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-10 h-10 bg-yellow-400/10 border border-yellow-400/30 rounded-lg flex items-center justify-center text-yellow-400">
                        <Icon className="w-5 h-5" />
                      </div>
                      <h3 className="text-base font-bold text-white">{m.title}</h3>
                    </div>
                    <p className="text-sm text-slate-400 leading-relaxed">{m.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Comunal Call to Action */}
        <section id="comuna" className="py-20 bg-gradient-to-br from-green-950 via-slate-900 to-slate-950 border-t border-slate-800 relative overflow-hidden">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <div className="w-16 h-16 bg-yellow-400/20 border border-yellow-400/40 rounded-2xl flex items-center justify-center mx-auto mb-6 text-yellow-300">
              <Globe className="w-8 h-8" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
              Construyendo Soberanía desde las Bases
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto mt-4 leading-relaxed">
              Forma parte de la red de productores y líderes comunales que están transformando la agricultura con transparencia y tecnología.
            </p>
            <div className="mt-8 flex justify-center space-x-4">
              <Link
                to="/register"
                className="bg-yellow-500 hover:bg-yellow-400 text-slate-950 font-bold px-8 py-4 rounded-xl shadow-xl shadow-yellow-500/20 transition-all hover:scale-105 inline-flex items-center space-x-2"
              >
                <span>Registrar Mi Unidad Productiva</span>
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Mandatory Footer */}
        <footer className="mt-auto bg-slate-950 border-t border-slate-800 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-xs text-slate-400 space-y-2">
            <p className="font-medium">
              &copy; Diseñado por ARIBWEBDEV.SITE | Desarrollado por Alexander Infante
            </p>
            <p className="text-slate-500 text-[11px]">
              SISCIAC &bull; Sistema de Información sobre Soberanía Alimentaria y Control de Insumos Agrícolas Comunas
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;
